import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ClipboardList, Plus, Pencil, AlertTriangle, Loader2, RotateCw, X, Check, Info } from 'lucide-react';
import ReadingForm from './ReadingForm';
import { ReadingItems } from './ReadingChips';
import DraftChip from '../common/DraftChip';
import useDraftAutosave from '../../hooks/useDraftAutosave';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import usePlanControl from '../../hooks/usePlanControl';
import { fetchDrafts, checkSection, setIdOfSection } from '../../redux/appointmentDraftSlice';
import { fetchVisitReadings, recordReading, correctReading } from '../../redux/checkSetSlice';
import { fetchVisitRecordReadings } from '../../redux/healthRecordSlice';
import {
    offeredSets, formValuesFrom, readingPayload, validateReadingValues, sameValues, hhmm, recorderLine,
} from '../../utils/checkSets';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// "CHECK READINGS" ON THE DOCTOR'S VISIT (check sets, Round A — Scene 4/5).
//
// What the nurse recorded before the visit is here; the doctor can correct it
// or add a reading of any other set they may use (their own + the
// hospital's). Everything typed follows the visit's drafts system exactly as
// the notes do:
//   · 2 s after the last change the set's values are PUT as the draft
//     section `checks.<setId>` (content: the `{ fieldKey: value }` map the
//     commit takes), and put back — amber "Unsaved draft restored" — when
//     the visit is opened again;
//   · "Save" on a set writes that one reading at once (same writer as the
//     nurse's POST) and retires its draft;
//   · "Save and continue" / "Save & complete" commit every pending set in the
//     SAME single request as the notes and the prescription (body.checks),
//     through `draftControlRef` — the same hand-off PrescriptionSection gives;
//   · before the invoice step MedicalHistory asks `checkRequired()` — the
//     server's `requiredMissing` — so a set marked "Required in every visit"
//     is filled first, and a 409 REQUIRED_CHECKS_MISSING from the completion
//     is shown here too (`showMissing`).
//
// THE READ-ONLY MODE (owner bug report, 26 Sep 2026: "after completing the
// visit the readings are not shown in the completed appointment's details").
// A visit's readings are shown wherever that visit's details are, for EVERY
// status — Booked, Completed, Rescheduling, NoShow, Cancelled when it has
// any. Writing is a separate question:
//   · edit mode — `editable` (the host says the visit is open for writing:
//     Booked, or Completed while Edit is on), the doctor's switch is on and
//     the server agrees (`enabled`). Correct / Add, drafts, the missing list.
//   · read-only — everything else: the readings as cards (set, who / when,
//     values, how many times corrected), no buttons, no drafts. Still
//     fetched; drawn only when the visit HAS readings. A switch turned off
//     later does not hide readings already taken (contract §2).
//   · the patient's own view (role `patient`, a family login included) —
//     the same cards, read out of the health record
//     (GET /health-record/:patientId/readings?visit=<appointmentId>), which
//     is the patient's door to readings (the recorder endpoint refuses
//     patients) and is not behind the check_sets feature.
//
// Nothing for the doctor when the hospital has no check_sets, their plan
// lacks it, or on a visit that is not the doctor's own.
//
// Labels always come from each reading's own snapshot, never the live set:
// editing a set never relabels a reading already taken.

function CheckReadingsBlock({ appointment, editable = false, mySwitch = null, draftControlRef = null, className = '' }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { has } = useTenantFeatures();
    const role = typeof localStorage !== 'undefined' ? localStorage.getItem('Role') : null;
    const myId = typeof localStorage !== 'undefined' ? localStorage.getItem('ID') : null;
    const appointmentId = appointment?.appointmentId || null;
    const ownsVisit = role === 'doctor'
        && !!appointment?.doctorId
        && String(appointment.doctorId) === String(myId)
        && appointment.clinicalHidden !== true;
    // 1 Oct 2026 (owner): and the doctor's plan carries check_sets — the
    // server refuses the readings (read and write) without it, so the block
    // and the completion gate it feeds both stand down.
    const checkSetsPlanOn = usePlanControl('check_sets');
    // The doctor's own visit, whatever its status: its readings are read
    // (and shown) even where nothing may be written.
    const active = Boolean(has('check_sets') && checkSetsPlanOn && ownsVisit && appointmentId);
    // What the completion gate asks about: an open visit of a doctor whose
    // switch is not known to be off.
    const gateActive = Boolean(active && mySwitch !== false && appointment.status !== 'Cancelled');
    // The patient (or a family login acting for them) reads the visit out of
    // the health record.
    const recordPatientId = role === 'patient' && appointmentId ? (appointment?.patientId || null) : null;

    const visit = useSelector((state) => (appointmentId ? state.checkSets?.visits?.[appointmentId] : null));
    const data = visit?.data || null;
    const recordVisit = useSelector((state) => (recordPatientId ? state.healthRecord?.visitReadings?.[appointmentId] : null));
    const editMode = Boolean(gateActive && editable && data && data.enabled !== false);
    const draftEnabled = editMode;
    const autosave = useDraftAutosave({ appointmentId: draftEnabled ? appointmentId : null, enabled: draftEnabled });
    const draftsState = useSelector((state) => state.appointmentDraft);

    // setId → { values, baseline, readingId, errors, serverError }
    const [forms, setForms] = useState({});
    const formsRef = useRef(forms);
    useEffect(() => { formsRef.current = forms; }, [forms]);
    const [missing, setMissing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [savingSet, setSavingSet] = useState(null);
    const blockRef = useRef(null);
    const restoredForRef = useRef(null);

    const sets = useMemo(() => offeredSets(data), [data]);
    const setById = useMemo(() => Object.fromEntries(sets.map((s) => [s.setId, s])), [sets]);
    const readings = useMemo(() => data?.readings || [], [data]);
    const liveBySet = useMemo(() => Object.fromEntries(readings.map((r) => [r.setId, r])), [readings]);

    // A new visit starts with nothing open.
    useEffect(() => {
        setForms({});
        formsRef.current = {};
        setMissing(null);
        setAdding(false);
        restoredForRef.current = null;
    }, [appointmentId]);

    useEffect(() => {
        if (active) dispatch(fetchVisitReadings({ appointmentId, force: true }));
    }, [active, appointmentId, dispatch]);

    useEffect(() => {
        if (recordPatientId) dispatch(fetchVisitRecordReadings({ patientId: recordPatientId, appointmentId }));
    }, [recordPatientId, appointmentId, dispatch]);

    // Leaving edit mode (Cancel on a completed visit's Edit, the visit
    // closed) drops the open forms: what was typed is either committed or
    // kept as a server draft by the host's dialog, and comes back from there
    // (the restore below) when editing starts again.
    const wasEditingRef = useRef(false);
    useEffect(() => {
        if (wasEditingRef.current && !editMode) {
            formsRef.current = {};
            setForms({});
            setAdding(false);
        }
        wasEditingRef.current = editMode;
    }, [editMode]);

    useEffect(() => {
        if (draftEnabled) dispatch(fetchDrafts({ appointmentId }));
    }, [draftEnabled, appointmentId, dispatch]);

    const baselineFor = (set, live) => (live ? readingPayload(set.fields, formValuesFrom(set.fields, live.values)) : {});

    // A reading THIS doctor saved after the draft was last written (the desk
    // modal, another device) is their later decision on that set: the draft
    // is stale, not unsaved work. Both times are the server's. Anybody else's
    // reading (the nurse's) never retires the doctor's typing.
    const ownReadingIsNewer = (live, draftAt) => {
        if (!live || live.recordedRole !== 'doctor' || !myId) return false;
        if (String(live.recordedByDoctorId || '') !== String(myId)) return false;
        const readAt = Date.parse(live.recordedAt || '');
        const typedAt = Date.parse(draftAt || '');
        return Number.isFinite(readAt) && Number.isFinite(typedAt) && readAt >= typedAt;
    };

    // Put unsaved check drafts back, once per visit, after both the drafts and
    // the visit's sets are in (a draft of a set that is gone is left alone; a
    // draft older than the doctor's own later reading of it is retired).
    useEffect(() => {
        if (!draftEnabled) { restoredForRef.current = null; return; }
        if (!data || !draftsState?.loaded || draftsState.appointmentId !== appointmentId) return;
        if (restoredForRef.current === appointmentId) return;
        restoredForRef.current = appointmentId;
        const restored = {};
        Object.entries(draftsState.drafts || {}).forEach(([section, draft]) => {
            const setId = setIdOfSection(section);
            const set = setId ? setById[setId] : null;
            const content = draft?.content;
            if (!set || !content || typeof content !== 'object' || Array.isArray(content)) return;
            const live = liveBySet[setId];
            if (ownReadingIsNewer(live, draft.updatedAt)) {
                autosave.discard(section);
                return;
            }
            const baseline = baselineFor(set, live);
            const values = formValuesFrom(set.fields, content);
            if (sameValues(readingPayload(set.fields, values), baseline)) return;
            restored[setId] = { values, baseline, readingId: live?.readingId || null, errors: {}, serverError: '' };
            autosave.markRestored(section, draft.updatedAt);
        });
        if (Object.keys(restored).length) setForms((prev) => ({ ...restored, ...prev }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftEnabled, draftsState, appointmentId, data]);

    const pendingMap = () => {
        const out = {};
        Object.entries(formsRef.current).forEach(([setId, f]) => {
            const set = setById[setId];
            if (!set) return;
            const payload = readingPayload(set.fields, f.values);
            if (Object.keys(payload).length === 0 || sameValues(payload, f.baseline)) return;
            out[setId] = payload;
        });
        return out;
    };

    const scrollIntoView = () => {
        try { blockRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }); } catch { /* jsdom */ }
    };

    const openForm = (setId, extra = {}) => {
        const set = setById[setId];
        if (!set) return;
        setForms((prev) => {
            if (prev[setId]) return { ...prev, [setId]: { ...prev[setId], ...extra } };
            const live = liveBySet[setId];
            const values = formValuesFrom(set.fields, live?.values);
            return {
                ...prev,
                [setId]: { values, baseline: baselineFor(set, live), readingId: live?.readingId || null, errors: {}, serverError: '', ...extra },
            };
        });
        setAdding(false);
    };

    const onChange = (setId, key, value) => {
        const current = formsRef.current[setId];
        const set = setById[setId];
        if (!current || !set) return;
        const values = { ...current.values };
        if (value === undefined) delete values[key];
        else values[key] = value;
        const errors = { ...current.errors };
        delete errors[key];
        delete errors._empty;
        const next = { ...formsRef.current, [setId]: { ...current, values, errors, serverError: '' } };
        formsRef.current = next;
        setForms(next);
        const payload = readingPayload(set.fields, values);
        autosave.schedule(checkSection(setId), payload, { clean: sameValues(payload, current.baseline) });
    };

    const closeForm = (setId) => {
        const f = formsRef.current[setId];
        const set = setById[setId];
        const section = checkSection(setId);
        const typed = f && set && !sameValues(readingPayload(set.fields, f.values), f.baseline);
        if (typed || autosave.chips[section]) autosave.discard(section);
        const next = { ...formsRef.current };
        delete next[setId];
        formsRef.current = next;
        setForms(next);
    };

    const saveForm = async (setId) => {
        const f = formsRef.current[setId];
        const set = setById[setId];
        if (!f || !set || savingSet) return;
        const found = validateReadingValues(set.fields, f.values);
        if (Object.keys(found).length) {
            setForms((prev) => ({ ...prev, [setId]: { ...prev[setId], errors: found } }));
            return;
        }
        const payload = readingPayload(set.fields, f.values);
        const live = liveBySet[setId];
        setSavingSet(setId);
        try {
            const res = live
                ? await dispatch(correctReading({ readingId: live.readingId, appointmentId, values: payload }))
                : await dispatch(recordReading({ appointmentId, setId, values: payload }));
            const ok = live ? correctReading.fulfilled.match(res) : recordReading.fulfilled.match(res);
            if (ok) {
                autosave.committed(checkSection(setId));
                const next = { ...formsRef.current };
                delete next[setId];
                formsRef.current = next;
                setForms(next);
                toast.success(live ? t('checkSets.correction_saved') : t('checkSets.reading_saved'));
                return;
            }
            const refusal = res.payload || {};
            const message = serverReason(refusal, t('checkSets.reading_save_failed'), i18n.language);
            setForms((prev) => ({
                ...prev,
                [setId]: {
                    ...prev[setId],
                    errors: refusal.field && set.fields.some((x) => x.key === refusal.field)
                        ? { ...(prev[setId]?.errors || {}), [refusal.field]: message }
                        : prev[setId]?.errors || {},
                    serverError: refusal.field ? '' : message,
                },
            }));
            toast.warn(message);
            if (refusal.code === 'READING_SUPERSEDED' || refusal.code === 'READING_CONFLICT') {
                dispatch(fetchVisitReadings({ appointmentId, force: true }));
            }
        } finally {
            setSavingSet(null);
        }
    };

    // The hand-off to MedicalHistory's single commit (see the header).
    useImperativeHandle(draftControlRef, () => ({
        hasPendingDraft: () => draftEnabled && Object.keys(pendingMap()).length > 0,
        pendingPayload: () => {
            if (!draftEnabled) return {};
            const map = pendingMap();
            let blocked = false;
            const next = { ...formsRef.current };
            Object.keys(map).forEach((setId) => {
                const found = validateReadingValues(setById[setId].fields, next[setId].values);
                if (Object.keys(found).length) {
                    blocked = true;
                    next[setId] = { ...next[setId], errors: found };
                }
            });
            if (blocked) {
                formsRef.current = next;
                setForms(next);
                scrollIntoView();
                return null;
            }
            return map;
        },
        markCommitted: () => {
            const ids = Object.keys(pendingMap());
            ids.forEach((setId) => autosave.clearLocal(checkSection(setId)));
            const next = { ...formsRef.current };
            ids.forEach((setId) => { delete next[setId]; });
            formsRef.current = next;
            setForms(next);
            if (appointmentId) dispatch(fetchVisitReadings({ appointmentId, force: true }));
        },
        flushDraft: () => autosave.flushAll(),
        pendingKey: () => {
            const map = pendingMap();
            return JSON.stringify(Object.keys(map).sort().map((k) => [k, map[k]]));
        },
        showRefusal: ({ section, field, message }) => {
            const setId = setIdOfSection(section);
            if (!setId || !setById[setId]) return;
            openForm(setId);
            setForms((prev) => {
                const f = prev[setId];
                if (!f) return prev;
                return {
                    ...prev,
                    [setId]: field
                        ? { ...f, errors: { ...f.errors, [field]: message } }
                        : { ...f, serverError: message },
                };
            });
            scrollIntoView();
        },
        checkRequired: async () => {
            if (!gateActive) return [];
            const res = await dispatch(fetchVisitReadings({ appointmentId, force: true }));
            if (!fetchVisitReadings.fulfilled.match(res) || res.payload?.enabled === false) return [];
            return Array.isArray(res.payload?.requiredMissing) ? res.payload.requiredMissing : [];
        },
        showMissing: (list) => {
            if (!gateActive) return;
            setMissing(Array.isArray(list) ? list : []);
            if (draftEnabled) (list || []).forEach((m) => { if (setById[m.setId] && !formsRef.current[m.setId]) openForm(m.setId); });
            scrollIntoView();
        },
    }));

    const header = (withHint) => (
        <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base sm:text-lg font-semibold text-[#2D8A8A]">
                <ClipboardList className="h-5 w-5" aria-hidden="true" />
                {t('checkSets.block_title')}
            </h2>
            {withHint && <p className="mt-0.5 text-xs text-gray-500">{t('checkSets.block_hint')}</p>}
        </div>
    );

    // One reading, as a card: set, who / when, values from its own snapshot.
    const readingCard = (r, { byLine, set = null, corrections = 0, canCorrect = false }) => (
        <article key={r.readingId} data-testid={`check-readings-reading-${r.readingId}`} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800">
                        <Check className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                        {r.setName}
                        {set?.requiredEveryVisit && (
                            <span className="rounded-full bg-[#D7A05C]/15 px-2 py-px text-[10px] font-bold text-[#9a6a2c]">{t('checkSets.required_badge')}</span>
                        )}
                    </p>
                    <p data-testid={`check-readings-by-${r.readingId}`} className="mt-0.5 text-[11px] text-gray-500">
                        {byLine}
                        {corrections > 0 && <span className="ml-1 text-gray-400">({t('checkSets.corrected_count', { count: corrections })})</span>}
                    </p>
                </div>
                {canCorrect && (
                    <button
                        type="button"
                        data-testid={`check-readings-correct-${r.readingId}`}
                        onClick={() => openForm(r.setId)}
                        disabled={Boolean(savingSet)}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                    >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.correct')}
                    </button>
                )}
            </div>
            <ReadingItems items={r.items} testId={`check-readings-items-${r.readingId}`} />
        </article>
    );

    // The patient's own view: the visit's readings out of the health record.
    if (recordPatientId) {
        const list = recordVisit?.patientId === recordPatientId ? recordVisit.readings || [] : [];
        if (list.length === 0) return null;
        return (
            <section
                data-testid="check-readings-block"
                data-mode="record"
                className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 via-white to-white p-4 shadow-sm ${className}`}
            >
                <div className="mb-3">{header(false)}</div>
                <div className="flex flex-col gap-3">
                    {list.map((r) => readingCard(r, { byLine: `${recorderLine(r, t)} · ${hhmm(r.recordedAt)}` }))}
                </div>
            </section>
        );
    }

    if (!active) return null;

    if (!data) {
        // Unknown switch + a failed read: say nothing (the doctor may not use
        // check sets at all). Known ON: say it could not be loaded — and, in
        // the read-only mode, draw nothing while it loads (most closed visits
        // have no readings; a spinner that turns into nothing is noise).
        if (mySwitch !== true) return null;
        if (visit?.error) {
            return (
                <div role="alert" data-testid="check-readings-load-error" className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-100 bg-red-50/60 p-3 ${className}`}>
                    <p className="flex items-center gap-1.5 text-sm text-red-700">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        {serverReason(visit.error, t('checkSets.readings_load_failed'), i18n.language)}
                    </p>
                    <button
                        type="button"
                        onClick={() => dispatch(fetchVisitReadings({ appointmentId, force: true }))}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.retry')}
                    </button>
                </div>
            );
        }
        if (!editable) return null;
        return (
            <div data-testid="check-readings-loading" className={`flex items-center gap-2 text-xs text-[#2D8A8A] ${className}`}>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('checkSets.loading')}
            </div>
        );
    }

    const superseded = data.superseded || [];
    const correctionsOf = (setId) => superseded.filter((x) => x.setId === setId).length;
    const doctorByLine = (r) => (
        <>
            {t('checkSets.recorded_by', { name: r.recordedByName || t(`checkSets.role_${r.recordedRole || 'staff'}`), time: hhmm(r.recordedAt) })}
            {' · '}{t(`checkSets.role_${r.recordedRole || 'staff'}`)}
        </>
    );

    // READ-ONLY: the readings, nothing to press.
    if (!editMode) {
        if (readings.length === 0) return null;
        return (
            <section
                ref={blockRef}
                data-testid="check-readings-block"
                data-mode="read"
                className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 via-white to-white p-4 shadow-sm ${className}`}
            >
                <div className="mb-3">{header(false)}</div>
                <div className="flex flex-col gap-3">
                    {readings.map((r) => readingCard(r, { byLine: doctorByLine(r), set: setById[r.setId], corrections: correctionsOf(r.setId) }))}
                </div>
            </section>
        );
    }

    const addable = sets.filter((s) => !forms[s.setId] && !liveBySet[s.setId]);
    const serverMissing = Array.isArray(data.requiredMissing) ? data.requiredMissing : [];
    const missingList = missing && missing.length ? missing : serverMissing;
    const busy = Boolean(savingSet);

    const missingLine = (m) => {
        const fields = (m.missingFields || []).map((f) => f.label).filter(Boolean);
        return fields.length
            ? t('checkSets.missing_set_fields', { set: m.name, fields: fields.join(', ') })
            : t('checkSets.missing_set_only', { set: m.name });
    };

    return (
        <section
            ref={blockRef}
            data-testid="check-readings-block"
            data-mode="edit"
            className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 via-white to-white p-4 shadow-sm ${className}`}
        >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                {header(true)}
                {addable.length > 0 && (
                    <button
                        type="button"
                        data-testid="check-readings-add"
                        aria-expanded={adding}
                        onClick={() => setAdding((v) => !v)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[#4ABDBD]/40 bg-white px-3 py-1.5 text-sm font-semibold text-[#2D8A8A] shadow-sm transition-colors hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                    >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t('checkSets.add_reading')}
                    </button>
                )}
            </div>

            {adding && (
                <div data-testid="check-readings-add-menu" role="group" aria-label={t('checkSets.choose_set')} className="mb-3 flex flex-wrap gap-2 rounded-xl border border-gray-100 bg-white p-2">
                    {addable.map((s) => (
                        <button
                            key={s.setId}
                            type="button"
                            data-testid={`check-readings-add-${s.setId}`}
                            onClick={() => openForm(s.setId)}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-[#4ABDBD] hover:text-[#2D8A8A]"
                        >
                            {s.name}
                        </button>
                    ))}
                </div>
            )}

            {missingList.length > 0 && (
                <div role="status" data-testid="check-readings-missing" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                        <Info className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.missing_heading')}
                    </p>
                    <ul className="list-disc pl-5 text-xs text-amber-900">
                        {missingList.map((m) => (
                            <li key={m.setId || m.name} data-testid={`check-readings-missing-${m.setId}`}>{missingLine(m)}</li>
                        ))}
                    </ul>
                </div>
            )}

            {sets.length === 0 && readings.length === 0 && (
                <p data-testid="check-readings-no-sets" className="text-xs text-gray-500">{t('checkSets.no_sets_hint')}</p>
            )}
            {sets.length > 0 && readings.length === 0 && Object.keys(forms).length === 0 && (
                <p data-testid="check-readings-empty" className="text-xs text-gray-500">{t('checkSets.no_readings_yet')}</p>
            )}

            <div className="flex flex-col gap-3">
                {readings.filter((r) => !forms[r.setId]).map((r) => {
                    const set = setById[r.setId];
                    return readingCard(r, { byLine: doctorByLine(r), set, corrections: correctionsOf(r.setId), canCorrect: !!set });
                })}

                {Object.entries(forms).map(([setId, f]) => {
                    const set = setById[setId];
                    if (!set) return null;
                    const section = checkSection(setId);
                    const saving = savingSet === setId;
                    return (
                        <article key={setId} data-testid={`check-readings-form-${setId}`} className="rounded-xl border border-[#4ABDBD]/40 bg-white p-3 shadow-sm">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800">
                                    {set.name}
                                    <span className="rounded-full bg-[#4ABDBD]/15 px-2 py-px text-[10px] font-bold text-[#2D8A8A]">
                                        {f.readingId ? t('checkSets.form_correction') : t('checkSets.form_new')}
                                    </span>
                                    {set.requiredEveryVisit && (
                                        <span className="rounded-full bg-[#D7A05C]/15 px-2 py-px text-[10px] font-bold text-[#9a6a2c]">{t('checkSets.required_badge')}</span>
                                    )}
                                </p>
                                <button
                                    type="button"
                                    data-testid={`check-readings-form-close-${setId}`}
                                    onClick={() => closeForm(setId)}
                                    disabled={saving}
                                    aria-label={t('checkSets.cancel')}
                                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </div>
                            <ReadingForm
                                set={set}
                                values={f.values}
                                errors={f.errors}
                                onChange={(key, value) => onChange(setId, key, value)}
                                disabled={saving}
                                idPrefix={`check-${setId}`}
                            />
                            {f.serverError && (
                                <p role="alert" data-testid={`check-readings-form-error-${setId}`} className="mt-2 text-xs text-red-600">{f.serverError}</p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                                <DraftChip
                                    section={section}
                                    chip={autosave.chips[section]}
                                    onDiscard={() => closeForm(setId)}
                                    disabled={saving}
                                />
                                <button
                                    type="button"
                                    data-testid={`check-readings-form-cancel-${setId}`}
                                    onClick={() => closeForm(setId)}
                                    disabled={saving}
                                    className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {t('checkSets.cancel')}
                                </button>
                                <button
                                    type="button"
                                    data-testid={`check-readings-form-save-${setId}`}
                                    onClick={() => saveForm(setId)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-1.5 text-sm font-semibold text-white shadow transition-all hover:shadow-md disabled:opacity-50"
                                >
                                    {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                                    {t('checkSets.save')}
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

export default CheckReadingsBlock;
